// Iter 31.211 — Public Spy Radio widget for the login page.
//
// Plays the pinned landing track via /api/public/radio/current. No auth.
// Auto-fetch on mount, autoplay attempt (most browsers will require
// user interaction for the first play — we expose a big "Tune in"
// button that satisfies the autoplay policy).
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Radio, Volume2, VolumeX } from "lucide-react";
import TrackTime from "@/components/TrackTime";
import { API } from "@/lib/api";

export default function PublicRadioPanel() {
    const [current, setCurrent] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);  // honour autoplay policy
    const [err, setErr] = useState("");
    const audioRef = useRef(null);

    useEffect(() => {
        fetch(`${API}/public/radio/current`)
            .then((r) => r.json())
            .then((d) => {
                if (!d?.available) {
                    setErr(d?.reason || "No track configured.");
                    return;
                }
                setCurrent(d);
            })
            .catch((e) => setErr(e.message));
    }, []);

    const toggle = () => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) { el.pause(); setPlaying(false); }
        else {
            setErr("");  // clear stale preload errors on user interaction
            el.muted = false;
            setMuted(false);
            el.play().then(() => setPlaying(true)).catch((e) => setErr(e.message));
        }
    };
    const toggleMute = () => {
        const el = audioRef.current;
        if (!el) return;
        el.muted = !el.muted;
        setMuted(el.muted);
    };

    // Only surface errors AFTER the user has attempted to play.
    const [hasInteracted, setHasInteracted] = useState(false);
    useEffect(() => { if (playing) setHasInteracted(true); }, [playing]);

    return (
        <div
            className="paper-card tape-corner relative w-full max-w-md px-8 pt-10 pb-8 tilt-r"
            data-testid="login-public-radio"
        >
            <div className="absolute -top-3 -left-6 classified-stamp stamp-in bg-stamp-red text-paper-light">
                Live · On Air
            </div>
            <div className="mb-4">
                <div className="label-typed">Channel · Spy Radio</div>
                <h2 className="font-heading text-3xl uppercase tracking-[0.05em] text-ink leading-none mt-2 flex items-center gap-2">
                    <Radio className="w-7 h-7 text-stamp-red animate-pulse" />
                    On Air
                </h2>
                <div className="font-body text-[11px] tracking-[0.2em] text-ink-soft uppercase mt-2">
                    Unicast feed · 24h rotation
                </div>
            </div>

            <div className="hairline my-5" />

            {err && hasInteracted && (
                <div className="border border-stamp-red/40 bg-stamp-red/5 text-stamp-red text-[11px] tracking-[0.15em] uppercase px-3 py-2 mb-4" data-testid="login-radio-error">
                    {err}
                </div>
            )}

            {current && (
                <>
                    <div className="mb-4">
                        <div className="font-heading text-lg text-ink leading-tight truncate" data-testid="login-radio-title">
                            {current.title}
                        </div>
                        <div className="font-body text-sm text-ink-soft mt-1 truncate" data-testid="login-radio-artist">
                            {current.artist}
                        </div>
                        <div className="font-mono text-[10px] text-ink-soft mt-2">
                            <TrackTime audioElement={audioRef.current} testId="login-radio-time" />
                        </div>
                    </div>

                    <audio
                        ref={audioRef}
                        src={`${API}${current.stream_url}`}
                        preload="none"
                        onEnded={() => setPlaying(false)}
                        onError={() => { if (hasInteracted) setErr("Stream error."); }}
                        muted={muted}
                        data-testid="login-radio-audio"
                    />

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={toggle}
                            className="flex-1 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.22em] text-sm py-3 hover:bg-ink transition-colors inline-flex items-center justify-center gap-2"
                            data-testid="login-radio-play"
                            aria-label={playing ? "Pause radio" : "Tune in"}
                        >
                            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            {playing ? "Pause" : "Tune in"}
                        </button>
                        <button
                            type="button"
                            onClick={toggleMute}
                            disabled={!playing}
                            className="border border-ink/30 px-3 py-3 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-30"
                            data-testid="login-radio-mute"
                            aria-label={muted ? "Unmute" : "Mute"}
                            title={muted ? "Unmute" : "Mute"}
                        >
                            {muted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                    </div>
                </>
            )}

            <div className="mt-6 label-typed leading-relaxed">
                <div>● Test mode — public radio enabled</div>
                <div className="mt-1 text-ink-soft text-[10px]">
                    Hit Tune In. Hand on the dial for a beat — sometimes the signal warms up slow.
                </div>
            </div>
        </div>
    );
}
